import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LOGO_URL } from '../lib/images';
import Footer from './Footer';
import useTheme from '../hooks/useTheme';
import {
  Home, Smile, BookOpen, Puzzle, Dumbbell, User,
  LogOut, Menu, X, ChevronRight, Brain, Crown, CalendarDays, Palette, Sun, Moon
} from 'lucide-react';

const navItems = [
  { to: '/', icon: Home, label: 'Home' },
  { to: '/mood', icon: Smile, label: 'Mood' },
  { to: '/programs', icon: Dumbbell, label: 'Programs' },
  { to: '/weekly-plan', icon: CalendarDays, label: 'Weekly Plan' },
  { to: '/gratitude', icon: BookOpen, label: 'Gratitude' },
  { to: '/puzzles', icon: Puzzle, label: 'Puzzles' },
  { to: '/doodle', icon: Palette, label: 'Doodle' },
  { to: '/personality', icon: Brain, label: 'Personality' },
  { to: '/premium', icon: Crown, label: 'Premium' },
  { to: '/profile', icon: User, label: 'Profile' },
];

export default function AppLayout({ children }) {
  const { user, logout, isGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isDark, toggle: toggleTheme } = useTheme();
  // Footer appears only on the Home dashboard; all other app pages stay focused.
  const showFooter = location.pathname === '/';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const displayName = user?.name || 'Guest';
  const displayEmail = user?.email || (isGuest ? 'Guest mode' : '');

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 border-r shrink-0 sticky top-0 h-screen"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="p-5 flex items-center gap-3">
          <img src={LOGO_URL} alt="Today" className="w-10 h-10 rounded-full object-cover" data-testid="sidebar-logo" />
          <span className="text-xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today</span>
        </div>
        <nav className="flex-1 px-3 space-y-1" data-testid="sidebar-nav">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive ? 'font-semibold' : 'hover:opacity-80'
                }`
              }
              style={({ isActive }) => ({
                background: isActive ? 'var(--primary)' : 'transparent',
                color: isActive ? 'var(--primary-fg)' : 'var(--text)',
              })}
              data-testid={`nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
              style={{ background: 'var(--secondary)', color: 'var(--secondary-fg)' }}>
              {displayName[0]?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>{displayName}</div>
              <div className="text-xs truncate" style={{ color: 'var(--muted-fg)' }}>{displayEmail}</div>
            </div>
          </div>
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm transition-all hover:opacity-80"
            style={{ color: 'var(--accent)' }}>
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
          <button onClick={toggleTheme} data-testid="theme-toggle"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm transition-all hover:opacity-90 mt-1"
            style={{ color: 'var(--muted-fg)', background: 'transparent' }}>
            <span className="flex items-center gap-2">
              {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              {isDark ? 'Dark mode' : 'Light mode'}
            </span>
            <span
              className="w-9 h-5 rounded-full relative transition-colors"
              style={{ background: isDark ? 'var(--primary)' : 'var(--border)' }}>
              <span
                className="absolute top-0.5 w-4 h-4 rounded-full transition-all"
                style={{
                  left: isDark ? '18px' : '2px',
                  background: 'var(--surface)',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                }}
              />
            </span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 border-b px-4 py-3 flex items-center justify-between"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2.5">
          <img src={LOGO_URL} alt="Today" className="w-8 h-8 rounded-full object-cover" />
          <span className="text-lg font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today</span>
        </div>
        <button onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle"
          style={{ color: 'var(--text)' }}>
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Nav Drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="absolute top-0 right-0 w-72 h-full border-l p-4 pt-16 space-y-1"
            style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
            onClick={e => e.stopPropagation()}>
            {navItems.map(item => (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium"
                style={({ isActive }) => ({
                  background: isActive ? 'var(--primary)' : 'transparent',
                  color: isActive ? 'var(--primary-fg)' : 'var(--text)',
                })}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}>
                <item.icon className="w-5 h-5" />
                {item.label}
                <ChevronRight className="w-4 h-4 ml-auto opacity-50" />
              </NavLink>
            ))}
            <button onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm w-full mt-4"
              style={{ color: 'var(--accent)' }}>
              <LogOut className="w-5 h-5" /> Sign Out
            </button>
            <button onClick={toggleTheme} data-testid="mobile-theme-toggle"
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm w-full mt-1"
              style={{ color: 'var(--muted-fg)' }}>
              {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              {isDark ? 'Light mode' : 'Dark mode'}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:overflow-auto overflow-x-hidden flex flex-col">
        <div className="pt-14 lg:pt-0 flex-1">
          {children}
        </div>
        {showFooter && <Footer />}
      </main>
    </div>
  );
}
